import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  KeyValuePairs,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Table,
  Toggle,
} from '@cloudscape-design/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';

import {
  createScorecardTrackingRun,
  getScorecardTrackingProfile,
  listScorecardTrackingRuns,
  rebuildScorecardTrackingHistory,
  type ScorecardTrackingRun,
  retryScorecardTrackingSources,
  updateScorecardTrackingProfile,
} from '../../common/api/scorecardTracking';
import { DashboardViewLayout } from '../../components/DashboardViewLayout';
import { LoadingOrError } from '../../components/Loading';
import { formatScorecardPercentile } from '../../common/scorecardTracking';
import { ScorecardSourceLabel } from '../../common/scorecardSources';
import { trackingStatusLabel } from '../dashboard-scorecard-watchlist';
import { ScorecardGrade } from '../../components/ScorecardIdentity';
import { StarsIcon } from '../../components/StarsIcon';

const ACTIVE_STATUSES = new Set(['initializing', 'backfilling', 'refreshing']);

function formatDate(value?: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

function formatPercent(value?: number | null): string {
  return value == null ? '—' : `${Math.round(value * 100)}%`;
}

function formatResponseTime(value?: number | null): string {
  if (value == null) return '—';
  return value < 1 ? '< 1 day' : `${value.toFixed(1)} days`;
}

function runStatusType(status: string): 'success' | 'error' | 'warning' | 'in-progress' {
  if (status === 'completed') return 'success';
  if (status === 'completed_partial') return 'warning';
  if (status === 'failed') return 'error';
  return 'in-progress';
}

function SourceRunList({ run }: { run: ScorecardTrackingRun }) {
  const sources = run.sources ?? [];
  if (sources.length === 0) {
    return (
      <Box color="text-body-secondary">
        {run.kind === 'history_rebuild'
          ? 'Reused the retained initial source collection'
          : 'Waiting for source collection'}
      </Box>
    );
  }
  return (
    <SpaceBetween size="xxxs">
      {sources.map((sourceRun) => (
        <div key={sourceRun.uuid} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <ScorecardSourceLabel source={sourceRun.source} />
          <StatusIndicator
            type={
              sourceRun.status === 'completed'
                ? 'success'
                : sourceRun.status === 'degraded'
                  ? 'warning'
                  : ['queued', 'collecting'].includes(sourceRun.status)
                    ? 'in-progress'
                    : 'stopped'
            }
          >
            {sourceRun.status.replaceAll('_', ' ')}
          </StatusIndicator>
        </div>
      ))}
    </SpaceBetween>
  );
}

export default function ScorecardTrackingDetailPage() {
  const { uuid } = useParams({ from: '/dashboard/scorecard/watchlist_/$uuid' });
  const queryClient = useQueryClient();
  const [selectedRun, setSelectedRun] = useState<ScorecardTrackingRun | null>(null);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const profileQuery = useQuery({
    queryKey: ['scorecard-watchlist-profile', uuid],
    queryFn: () => getScorecardTrackingProfile(uuid),
    refetchInterval: (query) =>
      query.state.data
      && (ACTIVE_STATUSES.has(query.state.data.status) || query.state.data.active_run)
        ? 4_000
        : false,
  });
  const runsQuery = useQuery({
    queryKey: ['scorecard-watchlist-runs', uuid],
    queryFn: () => listScorecardTrackingRuns(uuid),
    refetchInterval:
      profileQuery.data
      && (ACTIVE_STATUSES.has(profileQuery.data.status) || profileQuery.data.active_run)
        ? 4_000
        : false,
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['scorecard-watchlist-profile', uuid] }),
      queryClient.invalidateQueries({ queryKey: ['scorecard-watchlist-runs', uuid] }),
      queryClient.invalidateQueries({ queryKey: ['scorecard-watchlist'] }),
    ]);
  };
  const updateMutation = useMutation({
    mutationFn: (body: {
      ongoing_enabled?: boolean;
      public_enabled?: boolean;
      archived?: boolean;
    }) =>
      updateScorecardTrackingProfile(uuid, body),
    onSuccess: refresh,
  });
  const runMutation = useMutation({
    mutationFn: () => createScorecardTrackingRun(uuid),
    onSuccess: refresh,
  });
  const rebuildMutation = useMutation({
    mutationFn: () => rebuildScorecardTrackingHistory(uuid),
    onSuccess: refresh,
  });
  const retryMutation = useMutation({
    mutationFn: (run: ScorecardTrackingRun) =>
      retryScorecardTrackingSources(uuid, run.uuid, { sources: [] }),
    onSuccess: async () => {
      setSelectedRun(null);
      await refresh();
    },
  });

  if (profileQuery.isLoading || !profileQuery.data || profileQuery.error) {
    return (
      <DashboardViewLayout
        breadcrumbs={[
          { text: 'Scorecards', href: '/dashboard/scorecard' },
          { text: 'Watchlist', href: '/dashboard/scorecard/watchlist' },
          { text: 'Business', href: '' },
        ]}
      >
        <LoadingOrError
          loading={profileQuery.isLoading}
          error={profileQuery.error}
          retry={profileQuery.refetch}
        />
      </DashboardViewLayout>
    );
  }

  const profile = profileQuery.data;
  const metrics = profile.latest_snapshot?.metrics ?? [];
  const publicPath = profile.public_slug ? `/h/${profile.public_slug}` : null;
  const active = ACTIVE_STATUSES.has(profile.status) || Boolean(profile.active_run);
  const historyNeedsRebuild = metrics.some(
    (item) =>
      item.source === 'google'
      && (item.composite_grade == null || item.composite_percentile == null),
  );

  return (
    <DashboardViewLayout
      breadcrumbs={[
        { text: 'Scorecards', href: '/dashboard/scorecard' },
        { text: 'Watchlist', href: '/dashboard/scorecard/watchlist' },
        { text: profile.place?.name ?? 'Resolving business', href: '' },
      ]}
      contentType="dashboard"
    >
      <Modal
        visible={pauseOpen}
        onDismiss={() => !updateMutation.isPending && setPauseOpen(false)}
        header="Pause monthly Scorecard tracking?"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setPauseOpen(false)}>Keep tracking</Button>
              <Button
                variant="primary"
                loading={updateMutation.isPending}
                onClick={() =>
                  updateMutation.mutate(
                    { ongoing_enabled: false },
                    { onSuccess: () => setPauseOpen(false) },
                  )
                }
              >
                Pause tracking
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Existing snapshots and the public history remain available. No new monthly run will be
        scheduled until tracking is resumed.
      </Modal>

      <Modal
        visible={archiveOpen}
        onDismiss={() => !updateMutation.isPending && setArchiveOpen(false)}
        header={profile.status === 'archived' ? 'Restore this Watchlist business?' : 'Archive this Watchlist business?'}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setArchiveOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                loading={updateMutation.isPending}
                onClick={() =>
                  updateMutation.mutate(
                    { archived: profile.status !== 'archived' },
                    { onSuccess: () => setArchiveOpen(false) },
                  )
                }
              >
                {profile.status === 'archived' ? 'Restore' : 'Archive'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {profile.status === 'archived'
          ? 'Restore the business to manage it again. Monthly tracking remains paused until explicitly resumed.'
          : 'Archiving pauses monthly tracking and hides the public history route. Existing snapshots are retained.'}
      </Modal>

      <Modal
        visible={Boolean(selectedRun)}
        onDismiss={() => !retryMutation.isPending && setSelectedRun(null)}
        header={selectedRun ? `${selectedRun.kind.replaceAll('_', ' ')} run details` : 'Run details'}
        size="large"
        footer={
          selectedRun &&
          ['completed_partial', 'failed'].includes(selectedRun.status) &&
          (selectedRun.sources ?? []).some((source) =>
            ['failed', 'degraded', 'rejected_identity', 'unavailable'].includes(source.status),
          ) ? (
            <Box float="right">
              <Button
                variant="primary"
                loading={retryMutation.isPending}
                onClick={() => retryMutation.mutate(selectedRun)}
              >
                Retry failed sources
              </Button>
            </Box>
          ) : undefined
        }
      >
        {selectedRun ? (
          <SpaceBetween size="m">
            <KeyValuePairs
              columns={3}
              items={[
                { label: 'Status', value: selectedRun.status.replaceAll('_', ' ') },
                { label: 'Attempt', value: selectedRun.attempt.toLocaleString() },
                {
                  label: 'Duration',
                  value:
                    selectedRun.started_at && selectedRun.completed_at
                      ? `${Math.max(
                          0,
                          Math.round(
                            (new Date(selectedRun.completed_at).getTime() -
                              new Date(selectedRun.started_at).getTime()) /
                              60_000,
                          ),
                        )} minutes`
                      : '—',
                },
                {
                  label: 'Requested range',
                  value: `${formatDate(selectedRun.requested_since)} – ${formatDate(selectedRun.requested_until)}`,
                },
                { label: 'Error category', value: selectedRun.error_category ?? '—' },
                { label: 'Safe summary', value: selectedRun.error_summary ?? '—' },
              ]}
            />
            <Table
              variant="embedded"
              items={selectedRun.sources ?? []}
              empty={<Box textAlign="center">Source collection has not started.</Box>}
              wrapLines
              columnDefinitions={[
                {
                  id: 'source',
                  header: 'Source and status',
                  cell: (item) => (
                    <SpaceBetween size="xxxs">
                      <ScorecardSourceLabel source={item.source} />
                      <StatusIndicator
                        type={
                          item.status === 'completed'
                            ? 'success'
                            : item.status === 'degraded'
                              ? 'warning'
                              : ['queued', 'collecting'].includes(item.status)
                                ? 'in-progress'
                                : 'stopped'
                        }
                      >
                        {item.status.replaceAll('_', ' ')}
                      </StatusIndicator>
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'reviews',
                  header: 'Collected reviews',
                  cell: (item) => (
                    <SpaceBetween size="xxxs">
                      <Box>
                        <b>{item.unique_review_count?.toLocaleString() ?? '—'}</b> unique reviews
                      </Box>
                      <Box color="text-body-secondary">
                        {item.oldest_review_at
                          ? `${formatDate(item.oldest_review_at)} – ${formatDate(item.newest_review_at)}`
                          : 'No dated review range'}
                      </Box>
                      {item.lifetime_rating == null ? null : (
                        <SpaceBetween direction="horizontal" size="xs">
                          <Box variant="span">{item.lifetime_rating.toFixed(1)}</Box>
                          <StarsIcon stars={item.lifetime_rating} />
                          {item.source_link ? (
                            <Link href={item.source_link} external>
                              {item.lifetime_review_count?.toLocaleString() ?? '—'} lifetime reviews
                            </Link>
                          ) : (
                            <Box variant="span">
                              {item.lifetime_review_count?.toLocaleString() ?? '—'} lifetime reviews
                            </Box>
                          )}
                        </SpaceBetween>
                      )}
                    </SpaceBetween>
                  ),
                },
                {
                  id: 'termination',
                  header: 'Collection result',
                  cell: (item) => (
                    <SpaceBetween size="xxxs">
                      <Box>{item.terminal_reason?.replaceAll('_', ' ') ?? 'Still collecting'}</Box>
                      {item.provider ? (
                        <Box color="text-body-secondary">Provider: {item.provider}</Box>
                      ) : null}
                    </SpaceBetween>
                  ),
                },
              ]}
            />
          </SpaceBetween>
        ) : null}
      </Modal>

      <SpaceBetween size="l">
        <Container
          header={
            <Header
              variant="h1"
              description={profile.place?.address ?? profile.google_place_id}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  {profile.place ? (
                    <Button href={`/dashboard/places/${profile.place.uuid}`}>
                      Open place
                    </Button>
                  ) : null}
                  {publicPath && profile.published_at && profile.public_enabled ? (
                    <Button href={publicPath} target="_blank" iconAlign="right" iconName="external">
                      Open public history
                    </Button>
                  ) : null}
                  {profile.latest_scorecard_slug ? (
                    <Button href={`/${profile.latest_scorecard_slug}`} target="_blank">
                      Latest scorecard
                    </Button>
                  ) : null}
                  {historyNeedsRebuild ? (
                    <Button
                      iconName="refresh"
                      loading={rebuildMutation.isPending}
                      disabled={active || profile.status === 'archived'}
                      onClick={() => rebuildMutation.mutate()}
                    >
                      Rebuild history
                    </Button>
                  ) : null}
                  <Button
                    variant="primary"
                    iconName="refresh"
                    loading={runMutation.isPending}
                    disabled={active || profile.status === 'archived'}
                    onClick={() => runMutation.mutate()}
                  >
                    Run now
                  </Button>
                </SpaceBetween>
              }
            >
              {profile.place?.name ?? 'Resolving Google Place…'}
            </Header>
          }
        >
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Tracking state</Box>
              <StatusIndicator
                type={
                  profile.status === 'ready'
                    ? 'success'
                    : profile.status === 'ready_partial'
                      ? 'warning'
                      : profile.status === 'failed'
                        ? 'error'
                        : active
                          ? 'in-progress'
                          : 'stopped'
                }
              >
                {trackingStatusLabel(profile.status)}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Initial collection</Box>
              <Box>{profile.lookback_days} days</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Last successful run</Box>
              <Box>{formatDate(profile.last_successful_run_at)}</Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Next run</Box>
              <Box>{profile.ongoing_enabled ? formatDate(profile.next_run_at) : 'Paused'}</Box>
            </div>
          </ColumnLayout>
        </Container>

        {profile.active_run ? (
          <Alert type="info" header="Scorecard History is being updated">
            {profile.active_run.kind === 'initial_backfill'
              ? 'The 545-day source collection is running. Reviews, source matching, and twelve reconstructed monthly points can take longer than a normal scorecard.'
              : 'A new observed monthly point is being collected and calculated.'}
          </Alert>
        ) : null}
        {profile.status === 'failed' ? (
          <Alert type="error" header="The latest tracking run did not produce Google metrics">
            Review the run history below, then use Run now to retry when the source is available.
          </Alert>
        ) : null}
        {historyNeedsRebuild && !active ? (
          <Alert type="error" header="This stored history needs to be rebuilt">
            The source collection completed, but an older Watchlist calculation removed scorecard
            fields that are present in the point-in-time scorecard. Rebuild history reuses the
            retained reviews; it does not run another 545-day collection.
          </Alert>
        ) : null}
        {runMutation.isError || rebuildMutation.isError || updateMutation.isError ? (
          <Alert type="error" header="The Watchlist update failed">
            {(runMutation.error ?? rebuildMutation.error ?? updateMutation.error)?.message}
          </Alert>
        ) : null}

        <Container header={<Header variant="h2">Tracking and sharing</Header>}>
          <SpaceBetween size="m">
            <Toggle
              checked={profile.ongoing_enabled}
              disabled={updateMutation.isPending || profile.status === 'archived'}
              onChange={({ detail }) =>
                detail.checked
                  ? updateMutation.mutate({ ongoing_enabled: true })
                  : setPauseOpen(true)
              }
            >
              Refresh this business every {profile.cadence_days} days
            </Toggle>
            <Toggle
              checked={profile.public_enabled}
              disabled={updateMutation.isPending}
              onChange={({ detail }) =>
                updateMutation.mutate({ public_enabled: detail.checked })
              }
            >
              Allow the private-by-link public Scorecard History page
            </Toggle>
            <Box>
              <Button onClick={() => setArchiveOpen(true)}>
                {profile.status === 'archived' ? 'Restore business' : 'Archive business'}
              </Button>
            </Box>
            <KeyValuePairs
              columns={2}
              items={[
                {
                  label: 'Friendly public route',
                  value: publicPath ? (
                    <Link href={publicPath} external={Boolean(profile.published_at)}>
                      {publicPath}
                    </Link>
                  ) : (
                    'Assigned after Google Place resolution'
                  ),
                },
                {
                  label: 'Published',
                  value: profile.published_at
                    ? formatDate(profile.published_at)
                    : 'After the first usable snapshot',
                },
              ]}
            />
          </SpaceBetween>
        </Container>

        <Container>
          <Table
            variant="embedded"
            header={
              <Header
                variant="h2"
                description="The overall score and each of its four components, materialized by source and rolling window."
              >
                Latest snapshot
              </Header>
            }
            items={metrics}
            loading={active && metrics.length === 0}
            loadingText="Calculating the first snapshot"
            empty={<Box textAlign="center">No calculated metrics are available yet.</Box>}
            wrapLines
            columnDefinitions={[
              {
                id: 'slice',
                header: 'Scorecard slice',
                cell: (item) => (
                  <SpaceBetween size="xxxs">
                    <ScorecardSourceLabel source={item.source} />
                    <Box color="text-body-secondary">Past {item.window_days} days</Box>
                    <StatusIndicator type={item.coverage_state === 'complete' ? 'success' : 'warning'}>
                      {item.coverage_state === 'complete' ? 'Complete review window' : 'Partial review window'}
                    </StatusIndicator>
                  </SpaceBetween>
                ),
              },
              {
                id: 'scorecard',
                header: 'Scorecard',
                cell: (item) => (
                  <div className="flex flex-wrap items-center gap-3">
                    <ScorecardGrade
                      grade={item.composite_grade}
                      percentile={item.composite_percentile}
                      compact
                    />
                    <SpaceBetween size="xxxs">
                      {item.composite_percentile == null ? (
                        <StatusIndicator type={active ? 'in-progress' : 'error'}>
                          {active ? 'Recalculating score' : 'Rebuild required'}
                        </StatusIndicator>
                      ) : (
                        <Box fontWeight="bold">
                          {formatScorecardPercentile(item.composite_percentile)} percentile
                        </Box>
                      )}
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="span">{item.average_rating.toFixed(2)}</Box>
                        <StarsIcon stars={item.average_rating} />
                      </SpaceBetween>
                      <Box color="text-body-secondary">
                        {item.review_count.toLocaleString()} reviews in this window
                      </Box>
                    </SpaceBetween>
                  </div>
                ),
              },
              {
                id: 'response',
                header: 'Response and sample',
                cell: (item) => (
                  <SpaceBetween size="xxxs">
                    <Box>
                      <b>{formatPercent(item.response_rate)}</b> response rate
                    </Box>
                    <Box>
                      <b>{formatResponseTime(item.average_response_time_days)}</b> average response time
                    </Box>
                    <Box color="text-body-secondary">
                      {item.sample_size.toLocaleString()} rated reviews in the sample
                    </Box>
                  </SpaceBetween>
                ),
              },
            ]}
          />
        </Container>

        <Container>
          <Table
            variant="embedded"
            header={<Header variant="h2">Run history</Header>}
            loading={runsQuery.isLoading}
            loadingText="Loading run history"
            items={runsQuery.data ?? []}
            empty={<Box textAlign="center">No runs have been queued.</Box>}
            wrapLines
            columnDefinitions={[
              {
                id: 'run',
                header: 'Run and status',
                cell: (item) => (
                  <SpaceBetween size="xxxs">
                    <Button variant="inline-link" onClick={() => setSelectedRun(item)} wrapText={false}>
                      {item.kind.replaceAll('_', ' ')}
                    </Button>
                    <StatusIndicator type={runStatusType(item.status)}>
                      {item.status.replaceAll('_', ' ')}
                    </StatusIndicator>
                    <Box color="text-body-secondary">Attempt {item.attempt.toLocaleString()}</Box>
                  </SpaceBetween>
                ),
              },
              {
                id: 'timing',
                header: 'Collection window and timing',
                cell: (item) => (
                  <SpaceBetween size="xxxs">
                    <Box>
                      {new Date(item.requested_since).toLocaleDateString()} –{' '}
                      {new Date(item.requested_until).toLocaleDateString()}
                    </Box>
                    <Box color="text-body-secondary">Started: {formatDate(item.started_at)}</Box>
                    <Box color="text-body-secondary">Completed: {formatDate(item.completed_at)}</Box>
                  </SpaceBetween>
                ),
              },
              {
                id: 'sources',
                header: 'Source outcomes',
                cell: (item) => <SourceRunList run={item} />,
              },
            ]}
          />
        </Container>
      </SpaceBetween>
    </DashboardViewLayout>
  );
}
